import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";

export default function QuickAddDialog({
  open,
  onOpenChange,
  quickAddForm,
  handleQuickAddChange,
  handleQuickAddCategoryChange,
  handleQuickAddSubmit,
  categories,
  subCategories,
  groups,
  manufacturers,
  vendors,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-lg font-bold">Quick Add Item</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-2">
          {/* SKU */}
          <div>
            <Label className="text-xs">SKU *</Label>
            <Input
              value={quickAddForm.sku}
              onChange={(e) => handleQuickAddChange("sku", e.target.value)}
              className="h-8 mt-1"
              placeholder="Barcode/SKU"
            />
          </div>

          {/* Product Name */}
          <div>
            <Label className="text-xs">Product Name *</Label>
            <Input
              value={quickAddForm.name}
              onChange={(e) => handleQuickAddChange("name", e.target.value.toUpperCase())}
              className="h-8 mt-1"
              placeholder="Product name"
              data-testid="quick-add-name-input"
            />
          </div>

          {/* Cost, Price, GP, Units per Case Row */}
          <div className="grid grid-cols-4 gap-2">
            <div>
              <Label className="text-xs">Cost</Label>
              <Input
                type="number"
                step="0.01"
                value={quickAddForm.cost_price}
                onChange={(e) => handleQuickAddChange("cost_price", e.target.value)}
                className="h-8 mt-1"
                placeholder="0.00"
              />
            </div>
            <div>
              <Label className="text-xs">Price *</Label>
              <Input
                type="number"
                step="0.01"
                value={quickAddForm.retail_price}
                onChange={(e) => handleQuickAddChange("retail_price", e.target.value)}
                className="h-8 mt-1"
                placeholder="0.00"
              />
            </div>
            <div>
              <Label className="text-xs">GP%</Label>
              <Input
                type="number"
                step="0.1"
                value={quickAddForm.gp_percent}
                onChange={(e) => handleQuickAddChange("gp_percent", e.target.value)}
                className="h-8 mt-1"
                placeholder="35"
              />
            </div>
            <div>
              <Label className="text-xs">Units/Case</Label>
              <Input
                type="number"
                value={quickAddForm.units_per_case}
                onChange={(e) => handleQuickAddChange("units_per_case", e.target.value)}
                className="h-8 mt-1"
                placeholder="1"
                data-testid="quick-add-units-per-case-input"
              />
            </div>
          </div>

          {/* Category & Sub Category Row */}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Category *</Label>
              <select
                value={quickAddForm.category}
                onChange={(e) => handleQuickAddCategoryChange(e.target.value)}
                className="w-full h-8 mt-1 px-2 border rounded-md text-sm"
              >
                <option value="">Select category</option>
                {[...categories].sort((a, b) => a.name.localeCompare(b.name)).map((cat) => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </select>
            </div>
            <div>
              <Label className="text-xs">Sub Category</Label>
              <select
                value={quickAddForm.sub_category}
                onChange={(e) => handleQuickAddChange("sub_category", e.target.value)}
                className="w-full h-8 mt-1 px-2 border rounded-md text-sm"
              >
                <option value="">Select sub-category</option>
                {[...subCategories.filter((sc) => sc.category_id === quickAddForm.category)].sort((a, b) => a.name.localeCompare(b.name)).map((sc) => (
                  <option key={sc.id} value={sc.id}>{sc.name}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Group & MFG Row */}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Group</Label>
              <select
                value={quickAddForm.group}
                onChange={(e) => handleQuickAddChange("group", e.target.value)}
                className="w-full h-8 mt-1 px-2 border rounded-md text-sm"
              >
                <option value="">Select group</option>
                {[...groups].sort((a, b) => a.name.localeCompare(b.name)).map((g) => (
                  <option key={g.id} value={g.id}>{g.name}</option>
                ))}
              </select>
            </div>
            <div>
              <Label className="text-xs">Manufacturer</Label>
              <select
                value={quickAddForm.mfg}
                onChange={(e) => handleQuickAddChange("mfg", e.target.value)}
                className="w-full h-8 mt-1 px-2 border rounded-md text-sm"
              >
                <option value="">Select manufacturer</option>
                {[...manufacturers].sort((a, b) => a.name.localeCompare(b.name)).map((m) => (
                  <option key={m.id} value={m.id}>{m.name}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Vendor */}
          <div>
            <Label className="text-xs">Vendor</Label>
            <select
              value={quickAddForm.vendor_ids[0] || ""}
              onChange={(e) => handleQuickAddChange("vendor_ids", e.target.value ? [e.target.value] : [])}
              className="w-full h-8 mt-1 px-2 border rounded-md text-sm"
            >
              <option value="">Select vendor</option>
              {[...vendors].sort((a, b) => a.company_name.localeCompare(b.company_name)).map((v) => (
                <option key={v.id} value={v.id}>{v.company_name}</option>
              ))}
            </select>
          </div>

          {/* Flags Row */}
          <div className="grid grid-cols-3 gap-2">
            <div className="flex items-center justify-between p-2 bg-zinc-50 rounded">
              <Label className="text-xs">Tax 1</Label>
              <Switch checked={quickAddForm.tax_1} onCheckedChange={(v) => handleQuickAddChange("tax_1", v)} />
            </div>
            <div className="flex items-center justify-between p-2 bg-zinc-50 rounded">
              <Label className="text-xs">Tax 2</Label>
              <Switch checked={quickAddForm.tax_2} onCheckedChange={(v) => handleQuickAddChange("tax_2", v)} />
            </div>
            <div className="flex items-center justify-between p-2 bg-green-50 rounded">
              <Label className="text-xs">FS</Label>
              <Switch checked={quickAddForm.foodstamp} onCheckedChange={(v) => handleQuickAddChange("foodstamp", v)} />
            </div>
          </div>
        </div>

        {/* Buttons */}
        <div className="flex gap-3 mt-4">
          <Button variant="outline" className="flex-1" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            className="flex-1 bg-blue-600 hover:bg-blue-700"
            onClick={handleQuickAddSubmit}
            disabled={!quickAddForm.sku || !quickAddForm.name || !quickAddForm.retail_price || !quickAddForm.category}
          >
            Add & Add to Cart
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
